import { EXHIBITION_COLORS } from '@/shared/constants';

export const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

export interface ExhibitionColorInfo {
  key: string;
  name: string;
  hex: string;
}

export type ExhibitionColorMap = Record<string, { name: string; hex: string }>;

export function isHexExhibitionColor(value: string): boolean {
  return HEX_COLOR_REGEX.test(value);
}

export function isLegacyExhibitionColorCode(value: string): boolean {
  return value in EXHIBITION_COLORS;
}

export function normalizeHexColor(hex: string): string {
  if (!HEX_COLOR_REGEX.test(hex)) return hex;
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase();
  }
  return hex.toUpperCase();
}

export function getExhibitionColorInfo(value: string): ExhibitionColorInfo {
  if (isHexExhibitionColor(value)) {
    const hex = normalizeHexColor(value);
    return { key: hex, name: hex, hex };
  }

  if (isLegacyExhibitionColorCode(value)) {
    const info = EXHIBITION_COLORS[value as keyof typeof EXHIBITION_COLORS];
    return { key: value, name: info.name, hex: info.hex };
  }

  return {
    key: value,
    name: value,
    hex: EXHIBITION_COLORS.default.hex,
  };
}

/** Construit les options à partir du musée (hex ou codes legacy), avec repli sur le catalogue historique. */
export function buildExhibitionColorOptions(
  availableColors: string[] | undefined,
): ExhibitionColorInfo[] {
  if (!availableColors?.length) {
    return Object.entries(EXHIBITION_COLORS)
      .filter(([code]) => code !== 'default')
      .map(([code, info]) => ({ key: code, name: info.name, hex: info.hex }));
  }

  const options = availableColors
    .filter((value) => isHexExhibitionColor(value) || isLegacyExhibitionColorCode(value))
    .map((value) => getExhibitionColorInfo(value));

  if (options.length > 0) return options;

  return Object.entries(EXHIBITION_COLORS)
    .filter(([code]) => code !== 'default')
    .map(([code, info]) => ({ key: code, name: info.name, hex: info.hex }));
}

export function buildExhibitionColorMap(options: ExhibitionColorInfo[]): ExhibitionColorMap {
  return Object.fromEntries(options.map((option) => [option.key, { name: option.name, hex: option.hex }]));
}

export function getDefaultExhibitionColor(options: ExhibitionColorInfo[]): string {
  return options[0]?.key ?? 'default';
}

function parseHex(hex: string): [number, number, number] | null {
  const normalized = normalizeHexColor(hex);
  if (!HEX_COLOR_REGEX.test(normalized)) return null;
  return [
    parseInt(normalized.slice(1, 3), 16),
    parseInt(normalized.slice(3, 5), 16),
    parseInt(normalized.slice(5, 7), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((channel) => Math.round(Math.max(0, Math.min(255, channel))).toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase();
}

function adjustHex(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb[0] + amount, rgb[1] + amount, rgb[2] + amount);
}

/** Variables CSS pour les couleurs hex personnalisées (lecteur audio, transcription). */
export function getExhibitionThemeStyle(color: string | undefined): Record<string, string> | undefined {
  if (!color || !isHexExhibitionColor(color)) return undefined;

  const base = normalizeHexColor(color);
  return {
    '--exhibition-start-base': adjustHex(base, -35),
    '--exhibition-base': base,
    '--exhibition-progress-active': adjustHex(base, 40),
  };
}

export function getExhibitionThemeClassName(color: string | undefined, prefix: string): string {
  if (!color || color === 'default') return `${prefix}--default`;
  if (isHexExhibitionColor(color)) return `${prefix}--custom`;
  if (isLegacyExhibitionColorCode(color)) return `${prefix}--${color}`;
  return `${prefix}--default`;
}
